import type { QueryClient } from '@tanstack/react-query'
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import type { TrackerFetcher } from '@/provider/TrackerProvider.types'
import { useAuth } from '@/provider/useAuth'
import { useFetcher } from '@/provider/useFetcher'
import { apiFetch } from './api'
import type {
  PerformanceFilters,
  PerformanceOptions,
  PerformancePoint,
  PerformancePointsEnvelope,
  PerformanceSeries,
} from './performance.types'
import { appendTargetMarketIds } from './targetMarkets'
import type { RunStatus } from './types'

const FIVE_MINUTES_MS = 5 * 60_000
const PAGE_LIMIT = 250
const MAX_POINTS = 500
export const performanceKeys = {
  org: (orgId: string) => ['performance', orgId] as const,
  options: (orgId: string) => ['performance', orgId, 'options'] as const,
  pointsRoot: (orgId: string) => ['performance', orgId, 'points'] as const,
  points: (orgId: string, filters: PerformanceFilters) =>
    [
      'performance',
      orgId,
      'points',
      filters.brandId,
      filters.category,
      filters.targetMarketIds?.join('|') ?? filters.market,
      filters.liveSearch,
      filters.from,
      filters.to,
    ] as const,
}

function buildPointsQuery(
  filters: PerformanceFilters,
  cursor: string | null
): string {
  const search = new URLSearchParams()

  search.set('brandId', filters.brandId)
  search.set('category', filters.category)

  if (filters.targetMarketIds && filters.targetMarketIds.length > 0) {
    appendTargetMarketIds(search, filters.targetMarketIds)
  } else {
    search.set('market', filters.market)
  }

  search.set('liveSearch', String(filters.liveSearch))
  search.set('from', filters.from)
  search.set('to', filters.to)
  search.set('limit', String(PAGE_LIMIT))

  if (cursor) {
    search.set('cursor', cursor)
  }

  return search.toString()
}

async function fetchPerformancePage(
  fetcher: TrackerFetcher,
  filters: PerformanceFilters,
  cursor: string | null,
  signal?: AbortSignal
): Promise<PerformancePointsEnvelope> {
  const query = buildPointsQuery(filters, cursor)
  const page = await apiFetch<PerformancePointsEnvelope>(
    fetcher,
    `/api/performance?${query}`,
    signal ? { signal } : {}
  )

  if (!page) {
    throw new Error('performance points request returned an empty body')
  }

  return page
}

function sortPoints(points: PerformancePoint[]): PerformancePoint[] {
  return [...points].sort((a, b) => {
    if (a.measurementAt !== b.measurementAt) {
      return a.measurementAt < b.measurementAt ? -1 : 1
    }

    return a.runId < b.runId ? -1 : Number(a.runId > b.runId)
  })
}

// Fetches every page up to the 500-point cap before returning; a partial series never caches.
export async function fetchPerformanceSeries(
  fetcher: TrackerFetcher,
  filters: PerformanceFilters,
  signal?: AbortSignal
): Promise<PerformanceSeries> {
  const points: PerformancePoint[] = []
  let cursor: string | null = null

  do {
    const page = await fetchPerformancePage(fetcher, filters, cursor, signal)

    points.push(...page.points)

    cursor = page.nextCursor
  } while (cursor !== null && points.length < MAX_POINTS)

  if (points.length >= MAX_POINTS && cursor !== null) {
    return { kind: 'too_many', points: [] }
  }

  return { kind: 'ready', points: sortPoints(points) }
}

export function usePerformanceOptions() {
  const fetcher = useFetcher()
  const auth = useAuth()
  const orgId = auth?.orgId

  return useQuery({
    queryKey: orgId
      ? performanceKeys.options(orgId)
      : (['performance', 'disabled', 'options'] as const),
    queryFn: async ({ signal }) => {
      const res = await apiFetch<PerformanceOptions>(
        fetcher,
        '/api/performance/options',
        { signal }
      )

      return res ?? { brands: [] }
    },
    enabled: Boolean(orgId),
    staleTime: FIVE_MINUTES_MS,
    refetchOnWindowFocus: true,
  })
}

export function usePerformanceSeries(filters: PerformanceFilters | null) {
  const fetcher = useFetcher()
  const auth = useAuth()
  const orgId = auth?.orgId

  return useQuery({
    queryKey:
      orgId && filters
        ? performanceKeys.points(orgId, filters)
        : (['performance', 'disabled', 'points'] as const),
    queryFn: ({ signal }) => {
      if (!filters) {
        throw new Error('usePerformanceSeries requires resolved filters')
      }

      return fetchPerformanceSeries(fetcher, filters, signal)
    },
    enabled: Boolean(orgId) && filters !== null,
    staleTime: FIVE_MINUTES_MS,
    refetchOnWindowFocus: true,
    // A filter change mints a new query key, so the charts would unmount and remount without the previous series to hold.
    placeholderData: keepPreviousData,
  })
}

// Exported for the shell to call before switching organizations, so stale org data cannot linger.
export async function clearPerformanceOrgCache(
  queryClient: QueryClient,
  orgId: string
): Promise<void> {
  const key = performanceKeys.org(orgId)
  await queryClient.cancelQueries({ queryKey: key })
  queryClient.removeQueries({ queryKey: key })
}

export function usePerformanceOrgCacheLifecycle(
  orgId: string | undefined
): void {
  const queryClient = useQueryClient()
  const previousOrgIdRef = useRef<string | undefined>(orgId)

  useEffect(() => {
    const previousOrgId = previousOrgIdRef.current

    if (previousOrgId && previousOrgId !== orgId) {
      void clearPerformanceOrgCache(queryClient, previousOrgId)
    }
    previousOrgIdRef.current = orgId
  }, [orgId, queryClient])
}

export function useInvalidatePerformanceOnCompletion(
  runId: string | undefined,
  status: RunStatus | undefined
): void {
  const queryClient = useQueryClient()
  const auth = useAuth()
  const orgId = auth?.orgId
  const invalidatedRunIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!orgId || !runId || status !== 'complete') {
      return
    }

    if (invalidatedRunIdRef.current === runId) {
      return
    }

    invalidatedRunIdRef.current = runId

    queryClient.invalidateQueries({ queryKey: performanceKeys.options(orgId) })
    queryClient.invalidateQueries({
      queryKey: performanceKeys.pointsRoot(orgId),
    })
  }, [orgId, runId, status, queryClient])
}
